
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useIsMobile } from '@/hooks/use-mobile';
import { NodePanel } from './NodePanel';
import { useFlowEditor } from './hooks/useFlowEditor';
import { PolicyFlowHeader } from './PolicyFlowHeader';
import { PolicyFlowCanvas } from './PolicyFlowCanvas';

interface PolicyFlowEditorProps {
  policyId?: number;
}

export const PolicyFlowEditor = ({ policyId }: PolicyFlowEditorProps) => {
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  const [isNodePanelOpen, setIsNodePanelOpen] = useState(!isMobile);
  const [fullscreen, setFullscreen] = useState(false);
  
  const {
    nodes,
    edges,
    selectedNode,
    reactFlowWrapper,
    onNodesChange,
    onEdgesChange,
    onConnect,
    onNodeClick,
    onDragOver,
    onDrop,
    setReactFlowInstance,
    setSelectedNode,
    setNodes,
    handleSave,
    handleRun
  } = useFlowEditor(policyId);

  // Toggle fullscreen
  const toggleFullscreen = () => {
    setFullscreen(!fullscreen);
  };

  return (
    <div 
      className={`flex flex-col h-full ${fullscreen ? 'fixed inset-0 z-50 bg-background' : ''}`}
    >
      {/* Header */}
      <PolicyFlowHeader 
        policyId={policyId}
        fullscreen={fullscreen}
        toggleFullscreen={toggleFullscreen}
        handleSave={handleSave}
        handleRun={handleRun}
      />

      {/* Main content */}
      <div className="flex flex-1 overflow-hidden">
        {/* Flow editor */}
        <PolicyFlowCanvas
          nodes={nodes}
          edges={edges}
          onNodesChange={onNodesChange}
          onEdgesChange={onEdgesChange}
          onConnect={onConnect}
          onNodeClick={(event: React.MouseEvent, node: any) => {
            onNodeClick(event, node);
            if (isMobile) {
              setIsNodePanelOpen(true);
            }
          }}
          onDragOver={onDragOver}
          onDrop={onDrop}
          setReactFlowInstance={setReactFlowInstance}
          reactFlowWrapper={reactFlowWrapper}
          isNodePanelOpen={isNodePanelOpen}
          setIsNodePanelOpen={setIsNodePanelOpen}
        />

        {/* Node panel - shows available nodes for dragging or selected node properties */}
        {isNodePanelOpen && (
          <div className={`border-l w-full md:max-w-sm overflow-auto transition-all h-full ${isMobile ? 'absolute inset-0 z-40 bg-background' : ''}`}>
            <NodePanel 
              selectedNode={selectedNode} 
              setSelectedNode={setSelectedNode}
              setNodes={setNodes}
              onClose={() => setIsNodePanelOpen(false)}
              isMobile={isMobile}
            />
          </div>
        )}
      </div>
    </div>
  );
};
